import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Search, Download, ArrowLeft, Lock, Trash2 } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "albigny_admin_token";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const errMsg = (err, fallback) => {
    const d = err?.response?.data?.detail;
    return typeof d === "string" ? d : fallback;
};

const AdminLogin = ({ onLogged }) => {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            sessionStorage.setItem(TOKEN_KEY, data.token);
            onLogged(data.token);
        } catch (err) {
            toast.error(errMsg(err, "Connexion impossible."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="admin-login-form" className="max-w-sm mx-auto bg-white border border-brand-line p-8 sm:p-10 shadow-sm">
            <Lock className="text-[#8B5A2B] mb-4" size={22} strokeWidth={1.6} />
            <h1 className="font-serif text-3xl text-[#2C1E16] mb-2">Backoffice</h1>
            <p className="text-sm text-[#5C4033] mb-6">Accès réservé au restaurant.</p>
            <Input
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                required
                className="border-brand-line bg-[#F9F8F6] focus-visible:ring-[#5C4033] mb-4"
            />
            <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full py-5 bg-[#5C4033] hover:bg-[#2C1E16] text-[#F9F8F6] text-xs uppercase tracking-[0.3em] rounded-none">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Se connecter"}
            </Button>
        </form>
    );
};

const StatusBadge = ({ v, onToggle, busy }) => (
    <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        data-testid={`voucher-used-toggle-${v.voucher_number}`}
        title="Cliquer pour changer le statut"
        className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold border transition-colors duration-300 ${
            v.used
                ? "bg-[#2C1E16] text-[#F9F8F6] border-[#2C1E16] hover:bg-transparent hover:text-[#2C1E16]"
                : "bg-[#F1EBE1] text-[#5C4033] border-[#5C4033]/40 hover:bg-[#5C4033] hover:text-[#F9F8F6]"
        }`}
    >
        {busy ? "…" : v.used ? `Utilisé ${fmtDate(v.used_at)}` : "Non utilisé"}
    </button>
);

const PaidBadge = ({ v, onPay, busy }) =>
    v.paid ? (
        <span data-testid={`voucher-paid-badge-${v.voucher_number}`} className="inline-block px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#2F7D4F] text-white border border-[#2F7D4F] whitespace-nowrap">
            Payé {fmtDate(v.paid_at)}
        </span>
    ) : (
        <button
            type="button"
            onClick={onPay}
            disabled={busy}
            data-testid={`voucher-paid-btn-${v.voucher_number}`}
            className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold border border-[#2F7D4F]/50 text-[#2F7D4F] bg-white hover:bg-[#2F7D4F] hover:text-white transition-colors duration-300 whitespace-nowrap"
        >
            {busy ? "…" : "Marquer payé"}
        </button>
    );

const DeleteButton = ({ v, onConfirm }) => (
    <AlertDialog>
        <AlertDialogTrigger asChild>
            <button type="button" data-testid={`voucher-delete-btn-${v.voucher_number}`} title="Supprimer" className="p-2 border border-[#A33A2B]/40 text-[#A33A2B] hover:bg-[#A33A2B] hover:text-white transition-colors">
                <Trash2 size={15} />
            </button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="voucher-delete-dialog" className="rounded-none">
            <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl">Supprimer le bon n° {v.voucher_number} ?</AlertDialogTitle>
                <AlertDialogDescription>
                    Le bon de {v.full_name} sera définitivement effacé. Cette action est irréversible.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel data-testid="voucher-delete-cancel" className="rounded-none">Annuler</AlertDialogCancel>
                <AlertDialogAction data-testid="voucher-delete-confirm" onClick={onConfirm} className="rounded-none bg-[#A33A2B] hover:bg-[#7E2C21] text-white">
                    Supprimer
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

const VoucherRow = ({ v, token, onUpdated, onDeleted, onAuthError }) => {
    const [busy, setBusy] = useState(false);
    const [paying, setPaying] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const markPaid = async () => {
        setPaying(true);
        try {
            const { data } = await axios.post(`${API}/gift-voucher/${v.id}/paid`, {}, { headers });
            onUpdated(data);
            toast.success("Bon marqué comme payé.");
        } catch (err) {
            if (err?.response?.status === 401) return onAuthError();
            toast.error(errMsg(err, "Mise à jour impossible."));
        } finally {
            setPaying(false);
        }
    };

    const remove = async () => {
        try {
            await axios.delete(`${API}/gift-voucher/${v.id}`, { headers });
            onDeleted(v.id);
            toast.success(`Bon n° ${v.voucher_number} supprimé.`);
        } catch (err) {
            if (err?.response?.status === 401) return onAuthError();
            toast.error(errMsg(err, "Suppression impossible."));
        }
    };

    const toggle = async () => {
        setBusy(true);
        try {
            const { data } = await axios.patch(`${API}/gift-voucher/${v.id}/used`, { used: !v.used }, { headers });
            onUpdated(data);
            toast.success(data.used ? "Bon marqué comme utilisé." : "Bon remis en non utilisé.");
        } catch (err) {
            if (err?.response?.status === 401) return onAuthError();
            toast.error(errMsg(err, "Mise à jour impossible."));
        } finally {
            setBusy(false);
        }
    };

    const download = async () => {
        try {
            const res = await axios.get(`${API}/gift-voucher/${v.id}/pdf`, { headers, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Bon-cadeau-${v.voucher_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err) {
            if (err?.response?.status === 401) return onAuthError();
            toast.error("Téléchargement impossible.");
        }
    };

    return (
        <tr data-testid={`voucher-row-${v.voucher_number}`} className={`border-t border-brand-line align-top transition-colors ${v.paid ? "bg-[#E6F2EA]" : ""}`}>
            <td className={`py-4 px-2 pr-4 font-semibold whitespace-nowrap ${v.paid ? "text-[#2F7D4F]" : ""}`}>{v.voucher_number}</td>
            <td className="py-4 pr-4 whitespace-nowrap">{fmtDate(v.created_at)}</td>
            <td className="py-4 pr-4">
                <div className="font-medium">{v.full_name}</div>
                <div className="text-xs text-[#8B5A2B] break-all">{v.email}</div>
                <div className="text-xs text-[#8B5A2B]">{v.phone}</div>
            </td>
            <td className="py-4 pr-4 whitespace-nowrap">
                {v.voucher_type === "carte_blanche" ? "Carte Blanche" : `${Number(v.amount).toFixed(2)} €`}
            </td>
            <td className="py-4 pr-4 whitespace-nowrap">{fmtDate(v.valid_until)}</td>
            <td className="py-4 pr-4"><PaidBadge v={v} onPay={markPaid} busy={paying} /></td>
            <td className="py-4 pr-4"><StatusBadge v={v} onToggle={toggle} busy={busy} /></td>
            <td className="py-4 pr-2">
                <div className="flex gap-2">
                <button type="button" onClick={download} data-testid={`voucher-pdf-btn-${v.voucher_number}`} title="Télécharger le PDF" className="p-2 border border-[#5C4033]/40 text-[#5C4033] hover:bg-[#5C4033] hover:text-[#F9F8F6] transition-colors">
                    <Download size={15} />
                </button>
                <DeleteButton v={v} onConfirm={remove} />
                </div>
            </td>
        </tr>
    );
};

const VoucherHistory = ({ token, onLogout }) => {
    const [vouchers, setVouchers] = useState(null);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        axios
            .get(`${API}/gift-voucher`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setVouchers(r.data))
            .catch((err) => {
                if (err?.response?.status === 401) onLogout();
                else toast.error("Chargement impossible.");
            });
    }, [token, onLogout]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (vouchers || []).filter((v) => {
            if (filter === "used" && !v.used) return false;
            if (filter === "unused" && v.used) return false;
            if (filter === "paid" && !v.paid) return false;
            if (filter === "unpaid" && v.paid) return false;
            if (!q) return true;
            return [v.voucher_number, v.full_name, v.email, v.phone, v.message].some((f) => (f || "").toLowerCase().includes(q));
        });
    }, [vouchers, query, filter]);

    const onUpdated = (u) => setVouchers((list) => list.map((v) => (v.id === u.id ? u : v)));
    const onDeleted = (id) => setVouchers((list) => list.filter((v) => v.id !== id));
    const unusedCount = (vouchers || []).filter((v) => !v.used).length;
    const unpaidCount = (vouchers || []).filter((v) => !v.paid).length;

    return (
        <div data-testid="voucher-history">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.4em] text-[#8B5A2B] font-bold mb-3">— Backoffice</p>
                    <h1 className="font-serif text-4xl sm:text-5xl text-[#2C1E16]">Historique des bons cadeaux</h1>
                    {vouchers && (
                        <p data-testid="voucher-stats" className="mt-3 text-sm text-[#5C4033]">
                            {vouchers.length} bon(s) commandé(s) · {unusedCount} non utilisé(s) · {unpaidCount} non payé(s)
                        </p>
                    )}
                </div>
                <button type="button" onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C4033] hover:text-[#2C1E16]">
                    <LogOut size={14} /> Déconnexion
                </button>
            </div>

            <div className="flex flex-col md:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B5A2B]" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="N° de bon, nom, e-mail, tél…"
                        data-testid="voucher-search-input"
                        className="pl-9 border-brand-line bg-white focus-visible:ring-[#5C4033]"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    {[["all", "Tous"], ["unused", "Non utilisés"], ["used", "Utilisés"], ["unpaid", "Non payés"], ["paid", "Payés"]].map(([k, label]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setFilter(k)}
                            data-testid={`voucher-filter-${k}`}
                            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold border transition-colors ${
                                filter === k ? "bg-[#5C4033] text-[#F9F8F6] border-[#5C4033]" : "bg-white text-[#5C4033] border-brand-line hover:border-[#5C4033]"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-brand-line p-4 sm:p-6 overflow-x-auto">
                {!vouchers ? (
                    <Loader2 className="animate-spin mx-auto text-[#8B5A2B]" />
                ) : filtered.length === 0 ? (
                    <p data-testid="voucher-empty" className="text-center text-sm text-[#5C4033] py-8">Aucun bon cadeau trouvé.</p>
                ) : (
                    <table className="w-full text-sm text-[#2C1E16] min-w-[900px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#8B5A2B]">
                                <th className="pb-3 px-2 pr-4">N°</th>
                                <th className="pb-3 pr-4">Commandé le</th>
                                <th className="pb-3 pr-4">Client</th>
                                <th className="pb-3 pr-4">Formule</th>
                                <th className="pb-3 pr-4">Valable jusqu'au</th>
                                <th className="pb-3 pr-4">Paiement</th>
                                <th className="pb-3 pr-4">Statut</th>
                                <th className="pb-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((v) => (
                                <VoucherRow key={v.id} v={v} token={token} onUpdated={onUpdated} onDeleted={onDeleted} onAuthError={onLogout} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export const BackofficePage = () => {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
    const logout = React.useCallback(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
    }, []);

    return (
        <div className="App font-sans bg-[#F9F8F6] text-[#2C1E16]">
            <Navbar />
            <main className="bg-[#F1EBE1] min-h-screen pt-36 sm:pt-44 pb-24 px-6 sm:px-12 lg:px-24">
                <div className="max-w-6xl mx-auto">
                    <Link to="/bon-cadeau" data-testid="backoffice-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8B5A2B] hover:text-[#2C1E16] mb-8">
                        <ArrowLeft size={14} /> Retour au bon cadeau
                    </Link>
                    {token ? <VoucherHistory token={token} onLogout={logout} /> : <AdminLogin onLogged={setToken} />}
                </div>
            </main>
            <Footer />
        </div>
    );
};
